import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { 
  getLocations, createLocation, updateLocation, deleteLocation,
  getCategories, createCategory, updateCategory, deleteCategory,
  getRepairShops, createRepairShop, updateRepairShop, deleteRepairShop,
  getUsers, createUser, updateUser, deleteUser,
  getEmailSettings, testEmail,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MapPin, Tag, Wrench, Users, Loader2, Mail, Send, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';

const ROLES = [
  { value: 'ADMIN', label: 'Admin', color: 'bg-purple-100 text-purple-800' },
  { value: 'OPS_COORDINATOR', label: 'Ops Coordinator', color: 'bg-blue-100 text-blue-800' },
  { value: 'MANAGER', label: 'Manager', color: 'bg-green-100 text-green-800' },
  { value: 'ASSISTANT_MANAGER', label: 'Asst Manager', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'EMPLOYEE', label: 'Employee', color: 'bg-gray-100 text-gray-800' },
];

// Helper function to check if user can access settings (Admin or Ops)
const canAccessSettingsPage = (user) => {
  if (!user || !user.roles) return false;
  const roles = user.roles;
  return roles.includes('ADMIN') || roles.includes('OPS_COORDINATOR');
};

export default function Settings() {
  const { user, hasRole } = useAuth();
  const { refreshData, locations: appLocations } = useApp();
  
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [repairShops, setRepairShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Active/Inactive sub-tab state for each section
  const [locationActiveTab, setLocationActiveTab] = useState('active');
  const [categoryActiveTab, setCategoryActiveTab] = useState('active');
  const [shopActiveTab, setShopActiveTab] = useState('active');
  const [userActiveTab, setUserActiveTab] = useState('active');
  
  // Email settings
  const [emailSettings, setEmailSettings] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Modal states
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showShopModal, setShowShopModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Filtered lists based on active/inactive
  const activeLocations = locations.filter(l => l.is_active !== false);
  const inactiveLocations = locations.filter(l => l.is_active === false);
  const activeCategories = categories.filter(c => c.is_active !== false);
  const inactiveCategories = categories.filter(c => c.is_active === false);
  const activeShops = repairShops.filter(s => s.is_active !== false);
  const inactiveShops = repairShops.filter(s => s.is_active === false);
  const activeUsers = users.filter(u => u.is_active !== false);
  const inactiveUsers = users.filter(u => u.is_active === false);

  useEffect(() => {
    loadAllData();
    loadEmailSettings();
  }, []);
  
  const loadEmailSettings = async () => {
    try {
      const res = await getEmailSettings();
      setEmailSettings(res.data);
    } catch {
      setEmailSettings({ email_enabled: false });
    }
  };
  
  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    setSendingTestEmail(true);
    try {
      await testEmail(testEmailAddress);
      toast.success('Test email sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [locRes, catRes, shopRes, userRes] = await Promise.all([
        getLocations(),
        getCategories(),
        getRepairShops(),
        getUsers(),
      ]);
      setLocations(locRes.data);
      setCategories(catRes.data);
      setRepairShops(shopRes.data);
      setUsers(userRes.data);
    } catch (error) {
      toast.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  // Location handlers
  const openLocationModal = (item = null) => {
    setEditItem(item);
    setFormData(item ? { name: item.name, is_active: item.is_active } : { name: '', is_active: true });
    setShowLocationModal(true);
  };

  const handleLocationSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        await updateLocation(editItem.id, formData);
        toast.success('Location updated');
      } else {
        await createLocation(formData);
        toast.success('Location created');
      }
      setShowLocationModal(false);
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLocationDelete = async (id) => {
    if (!window.confirm('Deactivate this location?')) return;
    try {
      await deleteLocation(id);
      toast.success('Location deactivated');
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleLocationReactivate = async (id) => {
    try {
      await updateLocation(id, { is_active: true });
      toast.success('Location reactivated');
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error('Failed to reactivate');
    }
  };

  // Category handlers
  const openCategoryModal = (item = null) => {
    setEditItem(item);
    setFormData(item ? { 
      name: item.name, 
      is_active: item.is_active,
      allowed_relationship_types: item.allowed_relationship_types || ['STANDALONE', 'PARENT', 'CHILD']
    } : { 
      name: '', 
      is_active: true,
      allowed_relationship_types: ['STANDALONE', 'PARENT', 'CHILD']
    });
    setShowCategoryModal(true);
  };

  const handleCategorySave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.allowed_relationship_types || formData.allowed_relationship_types.length === 0) {
      toast.error('At least one relationship type must be allowed');
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        await updateCategory(editItem.id, formData);
        toast.success('Category updated');
      } else {
        await createCategory(formData);
        toast.success('Category created');
      }
      setShowCategoryModal(false);
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleRelationshipType = (type) => {
    const current = formData.allowed_relationship_types || [];
    if (current.includes(type)) {
      setFormData({ ...formData, allowed_relationship_types: current.filter(t => t !== type) });
    } else {
      setFormData({ ...formData, allowed_relationship_types: [...current, type] });
    }
  };

  const handleCategoryDelete = async (id) => {
    if (!window.confirm('Deactivate this category?')) return;
    try {
      await deleteCategory(id);
      toast.success('Category deactivated');
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleCategoryReactivate = async (id) => {
    try {
      await updateCategory(id, { is_active: true });
      toast.success('Category reactivated');
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error('Failed to reactivate');
    }
  };

  // Repair Shop handlers
  const openShopModal = (item = null) => {
    setEditItem(item);
    setFormData(item ? { name: item.name, is_active: item.is_active } : { name: '', is_active: true });
    setShowShopModal(true);
  };

  const handleShopSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        await updateRepairShop(editItem.id, formData);
        toast.success('Repair shop updated');
      } else {
        await createRepairShop(formData);
        toast.success('Repair shop created');
      }
      setShowShopModal(false);
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleShopDelete = async (id) => {
    if (!window.confirm('Deactivate this repair shop?')) return;
    try {
      await deleteRepairShop(id);
      toast.success('Repair shop deactivated');
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleShopReactivate = async (id) => {
    try {
      await updateRepairShop(id, { is_active: true });
      toast.success('Repair shop reactivated');
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error('Failed to reactivate');
    }
  };

  // User handlers
  const openUserModal = (item = null) => {
    setEditItem(item);
    setFormData(item ? {
      name: item.name,
      email: item.email,
      roles: item.roles || [],
      assigned_location_ids: item.assigned_location_ids || [],
      is_active: item.is_active,
    } : {
      name: '',
      email: '',
      password: '',
      roles: ['MANAGER'],
      assigned_location_ids: [],
    });
    setShowUserModal(true);
  };

  const toggleRole = (role) => {
    const currentRoles = formData.roles || [];
    if (currentRoles.includes(role)) {
      setFormData({ ...formData, roles: currentRoles.filter(r => r !== role) });
    } else {
      setFormData({ ...formData, roles: [...currentRoles, role] });
    }
  };

  const toggleLocation = (locationId) => {
    const currentLocations = formData.assigned_location_ids || [];
    if (currentLocations.includes(locationId)) {
      setFormData({ ...formData, assigned_location_ids: currentLocations.filter(l => l !== locationId) });
    } else {
      setFormData({ ...formData, assigned_location_ids: [...currentLocations, locationId] });
    }
  };

  const handleUserSave = async () => {
    if (!formData.name?.trim() || !formData.email?.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (!editItem && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }
    if (!formData.roles || formData.roles.length === 0) {
      toast.error('At least one role is required');
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          roles: formData.roles,
          assigned_location_ids: formData.assigned_location_ids || [],
          is_active: formData.is_active,
        };
        await updateUser(editItem.id, updateData);
        toast.success('User updated');
      } else {
        await createUser({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          roles: formData.roles,
          assigned_location_ids: formData.assigned_location_ids || [],
        });
        toast.success('User created');
      }
      setShowUserModal(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleUserDelete = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await deleteUser(id);
      toast.success('User deactivated');
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleUserReactivate = async (id) => {
    try {
      await updateUser(id, { is_active: true });
      toast.success('User reactivated');
      loadAllData();
      refreshData();
    } catch (error) {
      toast.error('Failed to reactivate');
    }
  };

  const getRoleColor = (role) => {
    const roleConfig = ROLES.find(r => r.value === role);
    return roleConfig?.color || 'bg-gray-100 text-gray-800';
  };

  // Use both context function AND direct role check for maximum reliability
  const canAccessSettings = canAccessSettingsPage(user) || hasRole('ADMIN') || hasRole('OPS_COORDINATOR');
  const isAdmin = user?.roles?.includes('ADMIN') || hasRole('ADMIN');

  if (!canAccessSettings) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">Only administrators and ops coordinators can access settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Settings</h1>
        <p className="text-gray-500">Manage locations, categories, repair shops, users, and email notifications</p>
      </div>

      <Tabs defaultValue="locations">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="locations" className="flex items-center gap-1" data-testid="tab-locations">
            <MapPin className="h-4 w-4" />
            <span className="hidden sm:inline">Locations</span>
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-1" data-testid="tab-categories">
            <Tag className="h-4 w-4" />
            <span className="hidden sm:inline">Categories</span>
          </TabsTrigger>
          <TabsTrigger value="shops" className="flex items-center gap-1" data-testid="tab-shops">
            <Wrench className="h-4 w-4" />
            <span className="hidden sm:inline">Repair Shops</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-1" data-testid="tab-users">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-1" data-testid="tab-email">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
        </TabsList>

        {/* Locations Tab */}
        <TabsContent value="locations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Locations</CardTitle>
              <Button onClick={() => openLocationModal()} className="bg-purple-600 hover:bg-purple-700" data-testid="add-location-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Location
              </Button>
            </CardHeader>
            <CardContent>
              <Tabs value={locationActiveTab} onValueChange={setLocationActiveTab} className="mb-4">
                <TabsList className="grid w-full grid-cols-2 max-w-xs">
                  <TabsTrigger value="active" className="text-sm">
                    Active ({activeLocations.length})
                  </TabsTrigger>
                  <TabsTrigger value="inactive" className="text-sm">
                    Inactive ({inactiveLocations.length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(locationActiveTab === 'active' ? activeLocations : inactiveLocations).map((loc) => (
                    <TableRow key={loc.id} className={loc.is_active === false ? 'bg-gray-50' : ''}>
                      <TableCell className="font-medium">{loc.name}</TableCell>
                      <TableCell>
                        <Badge className={loc.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {loc.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openLocationModal(loc)} data-testid={`edit-location-${loc.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {loc.is_active !== false ? (
                          <Button variant="ghost" size="icon" onClick={() => handleLocationDelete(loc.id)} data-testid={`delete-location-${loc.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" onClick={() => handleLocationReactivate(loc.id)} className="text-green-600 hover:text-green-700" data-testid={`reactivate-location-${loc.id}`}>
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(locationActiveTab === 'active' ? activeLocations : inactiveLocations).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-6 text-gray-500">
                        No {locationActiveTab} locations
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Equipment Categories</CardTitle>
              <Button onClick={() => openCategoryModal()} className="bg-purple-600 hover:bg-purple-700" data-testid="add-category-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </CardHeader>
            <CardContent>
              <Tabs value={categoryActiveTab} onValueChange={setCategoryActiveTab} className="mb-4">
                <TabsList className="grid w-full grid-cols-2 max-w-xs">
                  <TabsTrigger value="active" className="text-sm">
                    Active ({activeCategories.length})
                  </TabsTrigger>
                  <TabsTrigger value="inactive" className="text-sm">
                    Inactive ({inactiveCategories.length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Allowed Types</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(categoryActiveTab === 'active' ? activeCategories : inactiveCategories).map((cat) => (
                    <TableRow key={cat.id} className={cat.is_active === false ? 'bg-gray-50' : ''}>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(cat.allowed_relationship_types || ['STANDALONE', 'PARENT', 'CHILD']).map(type => (
                            <Badge key={type} variant="outline" className="text-xs">
                              {type === 'STANDALONE' ? 'S' : type === 'PARENT' ? 'P' : 'C'}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cat.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {cat.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openCategoryModal(cat)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {cat.is_active !== false ? (
                          <Button variant="ghost" size="icon" onClick={() => handleCategoryDelete(cat.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" onClick={() => handleCategoryReactivate(cat.id)} className="text-green-600 hover:text-green-700">
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(categoryActiveTab === 'active' ? activeCategories : inactiveCategories).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-6 text-gray-500">
                        No {categoryActiveTab} categories
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Repair Shops Tab */}
        <TabsContent value="shops">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Repair Shops</CardTitle>
              <Button onClick={() => openShopModal()} className="bg-purple-600 hover:bg-purple-700" data-testid="add-shop-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Shop
              </Button>
            </CardHeader>
            <CardContent>
              <Tabs value={shopActiveTab} onValueChange={setShopActiveTab} className="mb-4">
                <TabsList className="grid w-full grid-cols-2 max-w-xs">
                  <TabsTrigger value="active" className="text-sm">
                    Active ({activeShops.length})
                  </TabsTrigger>
                  <TabsTrigger value="inactive" className="text-sm">
                    Inactive ({inactiveShops.length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(shopActiveTab === 'active' ? activeShops : inactiveShops).map((shop) => (
                    <TableRow key={shop.id} className={shop.is_active === false ? 'bg-gray-50' : ''}>
                      <TableCell className="font-medium">{shop.name}</TableCell>
                      <TableCell>
                        <Badge className={shop.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {shop.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openShopModal(shop)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {shop.is_active !== false ? (
                          <Button variant="ghost" size="icon" onClick={() => handleShopDelete(shop.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" onClick={() => handleShopReactivate(shop.id)} className="text-green-600 hover:text-green-700">
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(shopActiveTab === 'active' ? activeShops : inactiveShops).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-6 text-gray-500">
                        No {shopActiveTab} repair shops
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Users</CardTitle>
              <Button onClick={() => openUserModal()} className="bg-purple-600 hover:bg-purple-700" data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </CardHeader>
            <CardContent>
              <Tabs value={userActiveTab} onValueChange={setUserActiveTab} className="mb-4">
                <TabsList className="grid w-full grid-cols-2 max-w-xs">
                  <TabsTrigger value="active" className="text-sm">
                    Active ({activeUsers.length})
                  </TabsTrigger>
                  <TabsTrigger value="inactive" className="text-sm">
                    Inactive ({inactiveUsers.length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead className="hidden sm:table-cell">Locations</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(userActiveTab === 'active' ? activeUsers : inactiveUsers).map((u) => (
                    <TableRow key={u.id} className={u.is_active === false ? 'bg-gray-50' : ''}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(u.roles || []).map(role => (
                            <Badge key={role} className={getRoleColor(role)}>
                              {role.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {(u.assigned_location_ids || []).length > 0
                          ? (u.assigned_location_ids || []).map(id => appLocations.find(l => l.id === id)?.name).filter(Boolean).join(', ')
                          : 'All'}
                      </TableCell>
                      <TableCell>
                        <Badge className={u.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {u.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openUserModal(u)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {u.id !== user.id && (
                          u.is_active !== false ? (
                            <Button variant="ghost" size="icon" onClick={() => handleUserDelete(u.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon" onClick={() => handleUserReactivate(u.id)} className="text-green-600 hover:text-green-700">
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(userActiveTab === 'active' ? activeUsers : inactiveUsers).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-gray-500">
                        No {userActiveTab} users
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Card */}
              <div className={`p-4 rounded-lg border ${emailSettings?.email_enabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-3">
                  {emailSettings?.email_enabled ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-amber-600" />
                  )}
                  <div>
                    <h3 className={`font-semibold ${emailSettings?.email_enabled ? 'text-green-800' : 'text-amber-800'}`}>
                      {emailSettings?.email_enabled ? 'Email Notifications Enabled' : 'Email Notifications Disabled'}
                    </h3>
                    <p className={`text-sm ${emailSettings?.email_enabled ? 'text-green-600' : 'text-amber-600'}`}>
                      {emailSettings?.email_enabled 
                        ? `Provider: ${emailSettings?.provider} • From: ${emailSettings?.sender_email}`
                        : 'Configure RESEND_API_KEY in backend/.env to enable email notifications'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notification Types */}
              <div>
                <h4 className="font-semibold mb-3">When emails are sent:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500" />
                    When a pending request (edit/removal) is approved or denied
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    When an asset is transferred to a manager's location
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    When a new edit or removal request is submitted (to Admin/Ops)
                  </li>
                </ul>
              </div>

              {/* Test Email */}
              {emailSettings?.email_enabled && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Send Test Email</h4>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      className="max-w-sm"
                      data-testid="test-email-input"
                    />
                    <Button 
                      onClick={handleSendTestEmail}
                      disabled={sendingTestEmail}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="send-test-email-btn"
                    >
                      {sendingTestEmail ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send Test
                    </Button>
                  </div>
                </div>
              )}

              {/* Setup Instructions */}
              {!emailSettings?.email_enabled && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Setup Instructions</h4>
                  <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                    <li>Sign up for a free <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">Resend</a> account</li>
                    <li>Get your API key from the Resend dashboard</li>
                    <li>Add <code className="bg-gray-100 px-1 rounded">RESEND_API_KEY=your_key</code> to <code className="bg-gray-100 px-1 rounded">backend/.env</code></li>
                    <li>Restart the backend service</li>
                  </ol>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Location Modal */}
      <Dialog open={showLocationModal} onOpenChange={setShowLocationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Location' : 'Add Location'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Location name"
                data-testid="location-name-input"
              />
            </div>
            {editItem && (
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Active</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLocationModal(false)}>Cancel</Button>
            <Button onClick={handleLocationSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-location-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Category name"
                data-testid="category-name-input"
              />
            </div>
            
            {/* Relationship Type Rules */}
            <div>
              <Label className="mb-2 block">Allowed Relationship Types *</Label>
              <p className="text-xs text-gray-500 mb-3">Define what types of parent-child relationships assets in this category can have.</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rel-standalone"
                    checked={(formData.allowed_relationship_types || []).includes('STANDALONE')}
                    onCheckedChange={() => toggleRelationshipType('STANDALONE')}
                  />
                  <Label htmlFor="rel-standalone" className="font-normal">
                    Standalone <span className="text-xs text-gray-500">(Not linked to anything)</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rel-parent"
                    checked={(formData.allowed_relationship_types || []).includes('PARENT')}
                    onCheckedChange={() => toggleRelationshipType('PARENT')}
                  />
                  <Label htmlFor="rel-parent" className="font-normal">
                    Parent <span className="text-xs text-gray-500">(Can have child assets linked)</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rel-child"
                    checked={(formData.allowed_relationship_types || []).includes('CHILD')}
                    onCheckedChange={() => toggleRelationshipType('CHILD')}
                  />
                  <Label htmlFor="rel-child" className="font-normal">
                    Child <span className="text-xs text-gray-500">(Can be linked to a parent asset)</span>
                  </Label>
                </div>
              </div>
            </div>
            
            {editItem && (
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Active</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>Cancel</Button>
            <Button onClick={handleCategorySave} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-category-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shop Modal */}
      <Dialog open={showShopModal} onOpenChange={setShowShopModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Repair Shop' : 'Add Repair Shop'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Shop name"
                data-testid="shop-name-input"
              />
            </div>
            {editItem && (
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Active</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShopModal(false)}>Cancel</Button>
            <Button onClick={handleShopSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-shop-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit User' : 'Add User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@company.com"
                data-testid="user-email-input"
              />
            </div>
            {!editItem && (
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Password"
                  data-testid="user-password-input"
                />
              </div>
            )}
            <div>
              <Label>Roles * (select one or more)</Label>
              <div className="space-y-2 mt-2">
                {ROLES.map((role) => (
                  <div key={role.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`role-${role.value}`}
                      checked={(formData.roles || []).includes(role.value)}
                      onCheckedChange={() => toggleRole(role.value)}
                    />
                    <label htmlFor={`role-${role.value}`} className="text-sm cursor-pointer">
                      {role.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Assigned Locations (for Managers)</Label>
              <p className="text-xs text-gray-500 mb-2">Admin and Ops have access to all locations</p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {appLocations.map((loc) => (
                  <div key={loc.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`loc-${loc.id}`}
                      checked={(formData.assigned_location_ids || []).includes(loc.id)}
                      onCheckedChange={() => toggleLocation(loc.id)}
                    />
                    <label htmlFor={`loc-${loc.id}`} className="text-sm cursor-pointer">
                      {loc.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            {editItem && (
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Active</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserModal(false)}>Cancel</Button>
            <Button onClick={handleUserSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-user-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
